/** What a person agreed to. Each is its own tick, because health data needs consent of its own (GDPR Art. 9). */
export enum LegalConsentKind {
  TERMS = "terms",
  HEALTH_DATA = "health_data",
}

/**
 * Where a person stands on one consent. `outdated` means they agreed to an older version of the
 * documents and are asked again. `withdrawn` is their own choice, so nothing nags them about it:
 * the features that need the consent stay closed until they turn it back on.
 */
export enum LegalConsentState {
  CURRENT = "current",
  OUTDATED = "outdated",
  MISSING = "missing",
  WITHDRAWN = "withdrawn",
}

export type LegalConsentStatus = Record<LegalConsentKind, LegalConsentState>;

/** Of the consents an app needs, the ones a person has to give before they can continue. */
export const legalConsentsToAsk = (status: LegalConsentStatus, kinds: LegalConsentKind[]) =>
  kinds.filter((kind) => status[kind] === LegalConsentState.OUTDATED || status[kind] === LegalConsentState.MISSING);

/** Accounts are for adults, everywhere (see the terms). */
export const MINIMUM_AGE = 18;

/** The newest birth year an account can hold. A year cannot tell more, so the exact check is at signup. */
export const latestAdultBirthYear = (now = new Date()) => now.getFullYear() - MINIMUM_AGE;

/** Whether a `YYYY-MM-DD` birth year is old enough. */
export const isAdultBirthYear = (birthDate: string, now = new Date()) =>
  Number(birthDate.slice(0, 4)) <= latestAdultBirthYear(now);

/** Whether someone born on this `YYYY-MM-DD` date has turned 18. Compared in UTC, as the date has no zone. */
export const isAdultBirthDate = (birthDate: string, now = new Date()) => {
  const birth = new Date(`${birthDate}T00:00:00.000Z`);
  if (Number.isNaN(birth.getTime())) return false;
  const adultFrom = new Date(birth);
  adultFrom.setUTCFullYear(birth.getUTCFullYear() + MINIMUM_AGE);
  return adultFrom.getTime() <= now.getTime();
};

/**
 * The date of the legal documents in force. It is the "last updated" on every document and is
 * saved with every consent, so it shows which wording someone agreed to. Move it with any change
 * to the documents' substance.
 */
export const LEGAL_DOCUMENTS_VERSION = "2026-11-03";

/** Where the documents can be read from outside the web apps, such as the mobile app. */
export const LEGAL_DOCUMENTS_URL = "https://www.complete-athlete.co/legal";

/** The details the legal documents state about who runs the platform. */
export enum LegalField {
  COMPANY = "company",
  ADDRESS = "address",
  NIP = "nip",
  REGON = "regon",
  KRS = "krs",
  EMAIL = "email",
  DMCA_AGENT = "dmcaAgent",
}

/**
 * The one place these details live: every document, in both apps and every language, reads them
 * from here. `null` is a detail not settled yet. The pages then show a marked blank and a notice,
 * and stay out of search until `isLegalEntityComplete()`.
 */
export const legalEntity: Record<LegalField, string | null> = {
  [LegalField.COMPANY]: null,
  [LegalField.ADDRESS]: null,
  [LegalField.NIP]: null,
  [LegalField.REGON]: null,
  [LegalField.KRS]: null,
  [LegalField.EMAIL]: "legal@complete-athlete.co",
  [LegalField.DMCA_AGENT]: null,
};

/** KRS is left out of the check: a sole trader has none. */
export const isLegalEntityComplete = () =>
  [LegalField.COMPANY, LegalField.ADDRESS, LegalField.NIP, LegalField.REGON, LegalField.EMAIL, LegalField.DMCA_AGENT].every(
    (field) => Boolean(legalEntity[field]),
  );

/** The registration numbers as a document states them, or null while none are set. */
export const legalRegistration = () =>
  [
    [LegalField.NIP, "NIP"],
    [LegalField.REGON, "REGON"],
    [LegalField.KRS, "KRS"],
  ]
    .flatMap(([field, label]) => {
      const value = legalEntity[field as LegalField];
      return value ? [`${label} ${value}`] : [];
    })
    .join(", ") || null;
